import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  clearAuthSession,
  fetchMe,
  finishPasskeyLogin,
  hasClientSessionHint,
  hasSessionCookie,
  installFetchUnauthorizedInterceptor,
  logoutRequest,
  registerUnauthorizedHandler,
  rememberPasskeyUiError,
  takePasskeyResume,
} from "../api/auth";
import {
  authIdentityKey,
  broadcastAuthChange,
  subscribeAuthChange,
} from "../auth/authSync";
import { setActiveCompanyRequest } from "../api/context";
import { resolveActiveCompany } from "../utils/activeCompanyGuard";
import {
  homePath,
  loginPath,
  normalizeAlias,
  persistLastAlias,
  readLastAlias,
} from "../utils/contractPath";

const TOKEN_KEY = "spm_token";
const USER_KEY = "spm_user";
/** Espelho do id da empresa ativa (performance); a API é a fonte da verdade. */
const ACTIVE_COMPANY_KEY = "spm_active_company_id";

function readUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persistUserSnapshot(u) {
  sessionStorage.setItem(USER_KEY, JSON.stringify(u));
  const alias = normalizeAlias(u?.cd_alias);
  if (alias) persistLastAlias(alias);
  if (u?.active_company_id != null) {
    sessionStorage.setItem(ACTIVE_COMPANY_KEY, String(u.active_company_id));
  } else {
    sessionStorage.removeItem(ACTIVE_COMPANY_KEY);
  }
}

const AuthContext = createContext(undefined);

/**
 * user: {
 *   nm_login, nm_user,
 *   cd_alias?: string,
 *   active_company_id?: number | null,
 *   primary_company_id?: number | null,
 *   permitted_companies?: { id_entity_company: number, nm_entity: string }[],
 *   enabled_modules?: string[],
 *   enabled_submodules?: string[],
 *   authorizations?: string[],
 * } | null
 */
export function AuthProvider({ children }) {
  const [authReady, setAuthReady] = useState(() => !hasClientSessionHint());
  const [user, setUser] = useState(() => {
    const u = readUser();
    // Só limpa snapshot se não há CSRF nem utilizador persistido (pós-login).
    if (!hasSessionCookie() && !u) {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
      sessionStorage.removeItem(ACTIVE_COMPANY_KEY);
      return null;
    }
    if (u?.cd_alias) persistLastAlias(u.cd_alias);
    return u;
  });
  const identityRef = useRef(authIdentityKey(user));
  const syncingRemoteRef = useRef(false);

  useEffect(() => {
    identityRef.current = authIdentityKey(user);
  }, [user]);

  const logout = useCallback(() => {
    const alias = normalizeAlias(user?.cd_alias) || readLastAlias();
    if (alias) persistLastAlias(alias);
    void logoutRequest();
    clearAuthSession();
    setUser(null);
    identityRef.current = "";
    if (!syncingRemoteRef.current) {
      broadcastAuthChange(null);
    }
  }, [user?.cd_alias]);

  useEffect(() => {
    installFetchUnauthorizedInterceptor();
    registerUnauthorizedHandler(() => {
      const alias =
        normalizeAlias(user?.cd_alias) ||
        normalizeAlias(readUser()?.cd_alias) ||
        readLastAlias();
      // Sessão já foi encerrada no servidor pelo interceptor; só limpa estado local.
      clearAuthSession();
      setUser(null);
      broadcastAuthChange(null);
      const target = loginPath(alias) || "/";
      if (typeof window !== "undefined" && window.location.pathname !== target) {
        window.location.assign(target);
      }
    });
    return () => registerUnauthorizedHandler(null);
  }, [user?.cd_alias]);

  const login = useCallback((_accessToken, userPayload) => {
    sessionStorage.removeItem(TOKEN_KEY);
    const merged = {
      ...userPayload,
      cd_alias: normalizeAlias(userPayload.cd_alias) || null,
      active_company_id:
        userPayload.active_company_id !== undefined
          ? userPayload.active_company_id
          : null,
      primary_company_id:
        userPayload.primary_company_id !== undefined
          ? userPayload.primary_company_id
          : null,
      permitted_companies: Array.isArray(userPayload.permitted_companies)
        ? userPayload.permitted_companies
        : [],
      enabled_modules: Array.isArray(userPayload.enabled_modules)
        ? userPayload.enabled_modules
        : [],
      enabled_submodules: Array.isArray(userPayload.enabled_submodules)
        ? userPayload.enabled_submodules
        : [],
      authorizations: Array.isArray(userPayload.authorizations)
        ? userPayload.authorizations
        : [],
    };
    persistUserSnapshot(merged);
    setUser(merged);
    setAuthReady(true);
    identityRef.current = authIdentityKey(merged);
    broadcastAuthChange(merged);
  }, []);

  useEffect(() => {
    const resume = takePasskeyResume("login");
    if (!resume || resume.kind !== "login" || !resume.token) return undefined;
    finishPasskeyLogin(resume.token, resume.step)
      .then((session) => {
        if (!session || (session.auth_step && session.auth_step !== "done")) return;
        if (!session.user && !hasSessionCookie()) return;
        login(null, {
          ...(session.user || {}),
          cd_alias: session.cd_alias || session.user?.cd_alias,
          active_company_id:
            session.active_company_id !== undefined
              ? session.active_company_id
              : null,
          primary_company_id:
            session.primary_company_id !== undefined
              ? session.primary_company_id
              : null,
          permitted_companies: session.permitted_companies ?? [],
          enabled_modules: session.enabled_modules ?? [],
          enabled_submodules: session.enabled_submodules ?? [],
          authorizations: session.authorizations ?? [],
        });
      })
      .catch((err) => {
        rememberPasskeyUiError(
          err instanceof Error
            ? err.message
            : "Não foi possível confirmar a passkey. Tente novamente."
        );
      });
    return undefined;
  }, [login]);

  const refreshSession = useCallback(async () => {
    const me = await fetchMe();
    let merged = null;
    setUser((prev) => {
      merged = prev
        ? {
            ...prev,
            nm_user: me.nm_user,
            nm_login: me.nm_login,
            id_user: me.id_user ?? prev?.id_user ?? null,
            cd_alias: normalizeAlias(me.cd_alias ?? prev?.cd_alias) || null,
            active_company_id: me.active_company_id ?? null,
            primary_company_id: me.primary_company_id ?? null,
            permitted_companies: me.permitted_companies ?? [],
            enabled_modules: me.enabled_modules ?? prev?.enabled_modules ?? [],
            enabled_submodules:
              me.enabled_submodules ?? prev?.enabled_submodules ?? [],
            authorizations: Array.isArray(me.authorizations)
              ? me.authorizations
              : prev?.authorizations ?? [],
          }
        : {
            nm_user: me.nm_user,
            nm_login: me.nm_login,
            id_user: me.id_user ?? null,
            cd_alias: normalizeAlias(me.cd_alias) || null,
            active_company_id: me.active_company_id ?? null,
            primary_company_id: me.primary_company_id ?? null,
            permitted_companies: me.permitted_companies ?? [],
            enabled_modules: me.enabled_modules ?? [],
            enabled_submodules: me.enabled_submodules ?? [],
            authorizations: Array.isArray(me.authorizations)
              ? me.authorizations
              : [],
          };
      persistUserSnapshot(merged);
      return merged;
    });
    // Updater do setState corre de forma síncrona no React 18 para este padrão;
    // se merged ainda for null, reconstrói a partir de `me`.
    if (!merged) {
      merged = {
        nm_user: me.nm_user,
        nm_login: me.nm_login,
        id_user: me.id_user ?? null,
        cd_alias: normalizeAlias(me.cd_alias) || null,
        active_company_id: me.active_company_id ?? null,
        primary_company_id: me.primary_company_id ?? null,
        permitted_companies: me.permitted_companies ?? [],
        enabled_modules: me.enabled_modules ?? [],
        enabled_submodules: me.enabled_submodules ?? [],
        authorizations: Array.isArray(me.authorizations)
          ? me.authorizations
          : [],
      };
      persistUserSnapshot(merged);
    }
    identityRef.current = authIdentityKey(merged);
    return merged;
  }, []);

  useEffect(() => {
    if (!hasClientSessionHint()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await refreshSession();
      } catch (e) {
        if (cancelled) return;
        if (e?.code === "UNAUTHORIZED") {
          const alias =
            normalizeAlias(readUser()?.cd_alias) || readLastAlias();
          logout();
          const target = loginPath(alias) || "/";
          if (typeof window !== "undefined" && window.location.pathname !== target) {
            window.location.assign(target);
          }
        }
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logout, refreshSession]);

  // Outras abas: cookie partilhado mas sessionStorage por aba — alinhar identidade.
  useEffect(() => {
    return subscribeAuthChange(async (payload) => {
      const remoteId = String(payload?.identity || "");
      const localBefore = identityRef.current;
      if (remoteId === localBefore) return;

      syncingRemoteRef.current = true;
      try {
        if (!payload?.loggedIn) {
          clearAuthSession();
          setUser(null);
          identityRef.current = "";
          const alias = readLastAlias();
          const target = loginPath(alias) || "/";
          if (window.location.pathname !== target) {
            window.location.assign(target);
          } else {
            window.location.reload();
          }
          return;
        }
        let merged;
        try {
          merged = await refreshSession();
        } catch (e) {
          if (e?.code === "UNAUTHORIZED") {
            clearAuthSession();
            setUser(null);
            identityRef.current = "";
            const alias = readLastAlias();
            window.location.assign(loginPath(alias) || "/");
            return;
          }
          throw e;
        }
        const next = authIdentityKey(merged);
        if (next === localBefore) return;
        // Identidade mudou: remonta shell/menus com o cookie actual.
        const alias = normalizeAlias(merged?.cd_alias) || readLastAlias();
        const home = homePath(alias);
        if (home && window.location.pathname !== home) {
          window.location.assign(home);
        } else {
          window.location.reload();
        }
      } finally {
        syncingRemoteRef.current = false;
      }
    });
  }, [refreshSession]);

  const switchActiveCompany = useCallback(
    async (id_entity_company) => {
      await setActiveCompanyRequest(id_entity_company);
      window.location.reload();
    },
    []
  );

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      refreshSession,
      switchActiveCompany,
      isAuthenticated: Boolean(user),
      authReady,
      activeCompanyId: (() => {
        const r = resolveActiveCompany(user);
        return r.isValid ? r.id : null;
      })(),
    }),
    [user, authReady, login, logout, refreshSession, switchActiveCompany]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return ctx;
}

/** Safe fora do AuthProvider (ex.: NavProvider em testes). */
export function useAuthOptional() {
  return useContext(AuthContext);
}
